import React, { useState, useEffect } from "react";
import { FaArrowLeft } from "react-icons/fa";
import "../assets/css/MyDetails.css";
import "../assets/css/Permissions.css";
import axiosInstance from "../components/api/axiosInstance";
import { getApiUrl, resolveImageUrl } from "../config/apiConfig";
import { getCurrentUserRole, getStoredRolePermissions } from "../utils/roleAccess";
import { getRolePermissions } from "./rolePermissionService";
import { getAuthItem, setAuthItem } from "../utils/authStorage";
import { formatPascal, applyPascalNameFields } from "../utils/formatters";
import { formValueWithMobileSanitize, mobileValidationMessage } from "../utils/phone";

function groupPermsByModule(perms) {
  if (!Array.isArray(perms) || perms.length === 0) return null;
  return perms.reduce((acc, p) => {
    const key = (p.Module_Name || p.module_name || p.Module || p.module || "Other").trim() || "Other";
    if (!acc[key]) acc[key] = [];
    const name = p.Permission_Name || p.permission_name || p.name || `#${p.Permission_Id ?? p.permission_id ?? p.id ?? ""}`;
    acc[key].push(name);
    return acc;
  }, {});
}

function MyDetails({ onBack, permissionsVersion = 0 }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    gender: "",
    dateOfBirth: "",
    phoneNumber: "",
    emergencyContactNumber: "",
    aadhaar: "",
    pan: "",
    address: "",
    company: "",
    companyEmail: "",
    role: "",
    department: "",
    employeeId: "",
    band: "",
    dateOfJoining: "",
    dateOfLeaving: "",
    status: "",
    companyLocation: "",
    profileImage: null,
    currentImageUrl: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const userRole = getCurrentUserRole();
  const canEditProfessional =
    userRole === "admin" || userRole === "hr";

  const [assignedModulesAndPerms, setAssignedModulesAndPerms] = useState(null);
  const [loadingPerms, setLoadingPerms] = useState(true);

  useEffect(() => {
    let mounted = true;
    const loadPermissions = async () => {
      const userStr = getAuthItem("user");
      let roleCount = 0;
      try {
        const u = userStr ? JSON.parse(userStr) : null;
        roleCount = Array.isArray(u?.roles) ? u.roles.length : 0;
      } catch (_) {}
      // Multi-role users: sign-in already stored the merged permissions of all roles.
      if (roleCount > 1) {
        setAssignedModulesAndPerms(groupPermsByModule(getStoredRolePermissions()));
        setLoadingPerms(false);
        return;
      }
      let roleId = null;
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          roleId =
            user?.roles?.[0]?.role_id ??
            user?.roles?.[0]?.Role_Id ??
            user?.roles?.[0]?.Designation_Id ??
            user?.roles?.[0]?.designation_id ??
            user?.role_id ??
            user?.Role_Id ??
            user?.roleId ??
            null;
        } catch (_) {}
      }
      const fromApi = roleId != null ? await getRolePermissions(roleId) : { data: [], fetched: false };
      if (!mounted) return;
      if (fromApi.fetched && Array.isArray(fromApi.data) && fromApi.data.length > 0) {
        const grouped = groupPermsByModule(fromApi.data);
        setAssignedModulesAndPerms(grouped);
        try {
          setAuthItem("user_role_permissions", JSON.stringify(fromApi.data));
        } catch (_) {}
      } else {
        const rk = roleId != null ? String(roleId) : "";
        let perms = null;
        try {
          const cacheRaw = localStorage.getItem("role_permissions_cache");
          if (cacheRaw && rk) {
            const cache = JSON.parse(cacheRaw);
            const cachedForRole = cache?.[rk];
            if (Array.isArray(cachedForRole) && cachedForRole.length > 0) {
              perms = cachedForRole.map((p) => ({
                Module_Name: p.module ?? p.Module_Name ?? p.module_name ?? "",
                Permission_Name: p.name ?? p.Permission_Name ?? p.permission_name ?? "",
                Permission_Id: p.id ?? p.Permission_Id ?? p.permission_id,
                module_name: p.module,
                permission_name: p.name,
                permission_id: p.id,
              }));
              try {
                setAuthItem("user_role_permissions", JSON.stringify(perms));
              } catch (_) {}
            }
          }
        } catch (_) {}
        if (!perms) {
          perms = getStoredRolePermissions();
        }
        setAssignedModulesAndPerms(groupPermsByModule(perms));
      }
      setLoadingPerms(false);
    };
    loadPermissions();
    return () => { mounted = false; };
  }, [permissionsVersion]);

  useEffect(() => {
    // Load user data from sessionStorage or API
    const loadUserData = async () => {
      try {
        const userStr = getAuthItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          // Fix image URL if it contains localhost
          const imageUrl = (user.image_url || "").length ? resolveImageUrl(user.image_url) : "";
          
          setFormData({
            firstName: user.first_name || "",
            lastName: user.last_name || "",
            email: user.email || "",
            gender: user.gender || "",
            dateOfBirth: user.date_of_birth || "",
            phoneNumber: user.phone_number || "",
            emergencyContactNumber: user.emergency_contact_number || "",
            aadhaar: user.aadhaar || "",
            pan: user.pan || "",
            address: user.address || "",
            company: user.company || "",
            companyEmail: user.company_email || "",
            role:
              user.designation_name ||
              user.Designation_Name ||
              (Array.isArray(user.roles) && user.roles[0]?.designation_name) ||
              user.role_name ||
              "",
            department: user.department || "",
            employeeId: user.employee_id || "",
            band: user.band || "",
            dateOfJoining: user.doj || "",
            dateOfLeaving: user.date_of_leaving || "",
            status: user.status || "",
            companyLocation: user.company_location || "",
            profileImage: null,
            currentImageUrl: imageUrl
          });
        }
      } catch (error) {
        console.error("Error loading user data:", error);
      }
    };

    loadUserData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === "file") {
      setFormData(prev => ({
        ...prev,
        [name]: files[0] || null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: formValueWithMobileSanitize(name, value)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const userStr = getAuthItem("user");
      const user = userStr ? JSON.parse(userStr) : null;
      const userId = user?.user_id ?? user?.id;
      if (!userId) {
        setError("User session not found. Please sign in again.");
        setLoading(false);
        return;
      }

      const phoneError = mobileValidationMessage(formData.phoneNumber, { label: "Phone number" });
      if (phoneError) {
        setError(phoneError);
        setLoading(false);
        return;
      }
      const emergencyError = mobileValidationMessage(formData.emergencyContactNumber, { label: "Emergency contact number" });
      if (emergencyError) {
        setError(emergencyError);
        setLoading(false);
        return;
      }

      let response;
      
      // If an image is selected, use FormData for multipart/form-data upload
      if (formData.profileImage) {
        const formDataToSend = new FormData();
        
        // Backend expects key 'image_path' for the file
        formDataToSend.append('image_path', formData.profileImage);
        
        // Append all other fields
        formDataToSend.append('first_name', formatPascal(formData.firstName));
        formDataToSend.append('last_name', formatPascal(formData.lastName));
        formDataToSend.append('email', formData.email);
        formDataToSend.append('company_email', formData.companyEmail);
        
        if (formData.gender) formDataToSend.append('gender', formData.gender);
        if (formData.dateOfBirth) formDataToSend.append('date_of_birth', formData.dateOfBirth);
        if (formData.phoneNumber) formDataToSend.append('phone_number', formData.phoneNumber);
        if (formData.emergencyContactNumber) formDataToSend.append('emergency_contact_number', formData.emergencyContactNumber);
        if (formData.aadhaar) formDataToSend.append('aadhaar', formData.aadhaar);
        if (formData.pan) formDataToSend.append('pan', formData.pan);
        if (formData.address) formDataToSend.append('address', formData.address);

        // Only admins/HR can modify professional details
        if (canEditProfessional) {
          if (formData.company) formDataToSend.append('company', formData.company);
          if (formData.department) formDataToSend.append('department', formData.department);
          if (formData.employeeId) formDataToSend.append('employee_id', formData.employeeId);
          if (formData.band) formDataToSend.append('band', formData.band);
          if (formData.dateOfJoining) formDataToSend.append('doj', formData.dateOfJoining);
          if (formData.dateOfLeaving) formDataToSend.append('date_of_leaving', formData.dateOfLeaving);
          if (formData.status) formDataToSend.append('status', formData.status);
          if (formData.companyLocation) formDataToSend.append('company_location', formData.companyLocation);
        }
        
        response = await axiosInstance.put(`/update-user/${userId}`, formDataToSend);
      } else {
        // No image selected, use regular JSON payload
        const updateData = applyPascalNameFields({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          gender: formData.gender,
          date_of_birth: formData.dateOfBirth,
          phone_number: formData.phoneNumber,
          emergency_contact_number: formData.emergencyContactNumber,
          aadhaar: formData.aadhaar,
          pan: formData.pan,
          address: formData.address,
          company_email: formData.companyEmail
        });

        if (canEditProfessional) {
          updateData.company = formData.company;
          updateData.department = formData.department;
          updateData.employee_id = formData.employeeId;
          updateData.band = formData.band;
          updateData.doj = formData.dateOfJoining;
          updateData.date_of_leaving = formData.dateOfLeaving;
          updateData.status = formData.status;
          updateData.company_location = formData.companyLocation;
        }

        response = await axiosInstance.put(`/update-user/${userId}`, updateData);
      }
      
      if (response.data && (response.data.status === "success" || response.status === 200 || response.data.user_id)) {
        // Update localStorage with new user data (backend returns user object directly)
        if (response.data.user_id) {
          const API_HOST = getApiUrl();
          let updatedUser = { ...response.data };
          if (updatedUser.image_url) {
            updatedUser.image_url = resolveImageUrl(updatedUser.image_url);
          }
          if (!updatedUser.image_url && updatedUser.image_path) {
            updatedUser.image_url = `${API_HOST.replace(/\/$/, "")}/uploads/cloud-office/users/${updatedUser.image_path}`;
          }
          setAuthItem("user", JSON.stringify(updatedUser));
          window.dispatchEvent(new Event('userDataUpdated'));
        } else if (response.data.data && response.data.data.user) {
          setAuthItem("user", JSON.stringify(response.data.data.user));
          window.dispatchEvent(new Event('userDataUpdated'));
        } else {
          console.error("[MyDetails] Update success but no user object in response:", response.data);
        }
        
        const resolvedImageUrl = response.data?.image_url || response.data?.data?.user?.image_url;
        setFormData(prev => ({
          ...prev,
          currentImageUrl: resolvedImageUrl
            ? resolveImageUrl(resolvedImageUrl || "")
            : prev.currentImageUrl,
          profileImage: null
        }));
        
        setSuccess("Details updated successfully!");
        alert("Successfully updated details!");
        
        setTimeout(() => {
          if (onBack) onBack();
        }, 1500);
      } else {
        console.error("[MyDetails] Update failed – unexpected response:", response?.data, "status:", response?.status);
        throw new Error(response.data?.message || "Update failed");
      }
    } catch (error) {
      console.error("[MyDetails] Error updating details:", error?.message);
      console.error("[MyDetails] Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      const errorMessage = error.response?.data?.message || error.message || "Failed to update details. Please try again.";
      alert(`Error: ${errorMessage}`);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mydetails-container">
      <div className="mydetails-header">
        <button className="back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
      </div>

      <form className="mydetails-form" onSubmit={handleSubmit}>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Personal Information */}
        <div className="form-section">
          <h2 className="section-title">Personal Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Last Name <span className="required">*</span></label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Gender</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Emergency Contact Number</label>
              <input
                type="tel"
                name="emergencyContactNumber"
                value={formData.emergencyContactNumber}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Aadhaar</label>
              <input
                type="text"
                name="aadhaar"
                value={formData.aadhaar}
                onChange={handleInputChange}
                maxLength="12"
              />
            </div>
            <div className="form-group">
              <label>PAN</label>
              <input
                type="text"
                name="pan"
                value={formData.pan}
                onChange={handleInputChange}
                maxLength="10"
              />
            </div>
            <div className="form-group full-width">
              <label>Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
              />
            </div>
          </div>
        </div>

        {/* Professional Information */}
        <div className="form-section">
          <h2 className="section-title">Professional Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Company</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              />
            </div>
            <div className="form-group">
              <label>Company Email</label>
              <input
                type="email"
                name="companyEmail"
                value={formData.companyEmail}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              />
            </div>
            <div className="form-group">
              <label>Designation</label>
              <input
                type="text"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                readOnly
              />
            </div>
            <div className="form-group">
              <label>Department</label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              />
            </div>
            <div className="form-group">
              <label>Employee ID</label>
              <input
                type="text"
                name="employeeId"
                value={formData.employeeId}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              />
            </div>
            <div className="form-group">
              <label>Band</label>
              <select
                name="band"
                value={formData.band}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              >
                <option value="">Select Band</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
                <option value="E">E</option>
              </select>
            </div>
            <div className="form-group">
              <label>Date of Joining</label>
              <input
                type="date"
                name="dateOfJoining"
                value={formData.dateOfJoining}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              />
            </div>
            <div className="form-group">
              <label>Date of Leaving</label>
              <input
                type="date"
                name="dateOfLeaving"
                value={formData.dateOfLeaving}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              >
                <option value="">Select Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Resigned">Resigned</option>
                <option value="Terminated">Terminated</option>
                <option value="On Hold">On Hold</option>
              </select>
            </div>
            <div className="form-group">
              <label>Company Location</label>
              <input
                type="text"
                name="companyLocation"
                value={formData.companyLocation}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              />
            </div>
            <div className="form-group">
              <label>Profile Image</label>
              <div className="file-input-wrapper">
                <input
                  type="file"
                  name="profileImage"
                  id="profileImage"
                  accept="image/*"
                  onChange={handleInputChange}
                  style={{ display: "none" }}
                />
                <label htmlFor="profileImage" className="file-input-label">
                  {formData.profileImage || formData.currentImageUrl ? "Change File" : "Choose File"}
                </label>
                <span className="file-name">
                  {formData.profileImage 
                    ? formData.profileImage.name 
                    : formData.currentImageUrl 
                      ? formData.currentImageUrl.split('/').pop() || formData.currentImageUrl
                      : "No file chosen"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Assigned Modules & Permissions */}
        <div className="form-section">
          <h2 className="section-title">Assigned Modules & Permissions</h2>
          {loadingPerms ? (
            <p className="permissions-muted">Loading permissions...</p>
          ) : assignedModulesAndPerms && Object.keys(assignedModulesAndPerms).length > 0 ? (
            <div className="mydetails-permissions-wrap">
              <div className="permissions-user-access">
                {Object.keys(assignedModulesAndPerms)
                  .sort((a, b) => a.localeCompare(b))
                  .map((moduleName) => (
                    <div key={moduleName} className="permissions-user-module">
                      <span className="permissions-user-module-name">{moduleName}:</span>
                      <span className="permissions-user-module-perms">
                        {assignedModulesAndPerms[moduleName]
                          .slice()
                          .sort((a, b) => a.localeCompare(b))
                          .join(", ")}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <p className="permissions-muted">No modules or permissions assigned.</p>
          )}
        </div>

        <div className="mydetails-actions">
          <button type="submit" className="btn-register" disabled={loading}>
            {loading ? "Updating..." : "Update Details"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default MyDetails;
